import type { Option } from "@tento-lona/sheets";
import { DebouncedRequests } from "@tento-core/debounced-requests";
import type { TypedApi } from "@tento-core/typed-api";
import { RowWriteError, SdkRequestError } from "../errors.ts";
import type { Network } from "../network.ts";
import { RowKey, RowNetworkKey, type SheetNetworkKey } from "@tento-lona/sheets";

/**
 * `Durable<T, S>` — what every durable write returns — is vocabulary shared
 * with the rows side: declared in `@tento/lona/sheets` (`sheets/src/core/durable.ts`)
 * and re-exported here, so `@tento/lona` and `@tento/lona/sheets` name one
 * type.
 */
export { Durable } from "@tento-lona/sheets";

/** One durable write waiting on its debounce key (`DurableHandoffs.handOff`). */
type DurableWaiter = {
  /** The write's own id, as handed to the executor. */
  readonly requestId: string;
  failure: Option<DebouncedRequests.Failure>;
};

/**
 * The durable (debounced) writes of one kind still waiting on their verdict,
 * by debounce key — the claim side of a client's durable-failure routing
 * (`ClientConfig.debounceExecutor`). A durable handoff returns no response,
 * so a refusal is matched back to the writes it carried here: the owner
 * wraps each handoff in `handOff`, and the client offers every reported
 * failure to `report`.
 *
 * Owned per client, each made by the client's `DurableRegistries` (which
 * `dispose` fails as a whole): `EventsTable` keeps one for event updates,
 * the client one for durable cell writes and row updates.
 */
export class DurableHandoffs {
  readonly #waiters = new Map<string, Set<DurableWaiter>>();
  /** Per-owner prefix + counter for `requestId`s — distinct across reloads
   *  too, since a service worker outlives the page that queued (not
   *  `crypto.randomUUID`, which an insecure context lacks). */
  readonly #session = `${Date.now().toString(36)}.${
    Math.random().toString(36).slice(2)
  }`;
  #seq = 0;

  /** A fresh id for one durable write — see `handOff`. */
  nextRequestId(): string {
    return `${this.#session}:${++this.#seq}`;
  }

  /**
   * Run a durable write's `handoff` under debounce `key` and resolve with
   * the refusal reported for THIS write while it was in flight (`report`),
   * or `null` when none was.
   *
   * `requestId` (from `nextRequestId`) is the write's own id; the handoff
   * passes it to the executor (`DebounceOptions.requestId`). A refusal
   * names the writes coalesced into the execution that was refused
   * (`Failure.requestIds`) and reaches only those — a request for the same
   * key that the executor dispatched separately has its own outcome. A
   * refusal from an executor that does not correlate (`requestIds` null)
   * reaches every write waiting on the key. No write that starts after the
   * report hears it.
   *
   * What makes the verdict THIS write's own — the executor's half of the
   * contract, which this method relies on and cannot check:
   * - (ii) the handoff's promise settles when the execution that carried the
   *   write finishes — not the first execution of its key, which a write
   *   queued mid-fetch does not belong to (`@tento-ui/debounce` hands each
   *   execution its own completion). Membership (`requestIds`) and
   *   completion are taken in the same tick, so they name the same writes.
   * - (iii) a refusal is reported before the handoff of ANY write the
   *   execution carried returns (`report` marks every carried waiter at
   *   once; the bridge reports on the first reply to arrive).
   * - (iv) a write whose transport gives no verdict is never reported as
   *   accepted: the executor reports status `0` for it before its handoff
   *   returns (a failed fetch; the bridge when the worker's reply times out
   *   or the message cannot be delivered), or the handoff throws. Resolving
   *   `null` here states that the execution carrying the write ANSWERED
   *   without refusing it.
   */
  async handOff(
    key: string,
    requestId: string,
    handoff: () => void | Promise<void>,
  ): Promise<Option<DebouncedRequests.Failure>> {
    const waiter: DurableWaiter = { requestId, failure: null };
    let waiters = this.#waiters.get(key);
    if (waiters == null) {
      waiters = new Set();
      this.#waiters.set(key, waiters);
    }
    waiters.add(waiter);
    try {
      await handoff();
      return waiter.failure;
    } finally {
      waiters.delete(waiter);
      if (waiters.size === 0 && this.#waiters.get(key) === waiters) {
        this.#waiters.delete(key);
      }
    }
  }

  /**
   * The fields a partial PATCH body writes, sorted and joined: every key it
   * carries a value for, an explicit `null` (a clear) included — a key whose
   * value is `undefined` never reaches the wire. The field-set half of a
   * durable PATCH's debounce key (`rowPatchKey`, `sheetPatchKey`), as
   * `event-update:` keys carry theirs (`EventOps.updateDurable`).
   */
  static patchFields(patch: object): string {
    return Object.entries(patch)
      .filter(([, value]) => value !== undefined)
      .map(([field]) => field)
      .sort()
      .join(",");
  }

  /**
   * The debounce key of a durable row update: the row AND the set of fields
   * the patch writes (`patchFields`). The executor sends only the last body
   * of a key, so two patches coalesce only when they write the same fields —
   * the body sent then carries every field of the ones it replaced, with the
   * newer values (the server replaces each top-level field whole, `attributes`
   * included). A `{ label }` and a `{ heightPx }` patch never share a key and
   * are sent as separate requests, so neither drops the other's field
   *. A refusal names the row by its URL.
   */
  static rowPatchKey(
    rowKey: RowNetworkKey,
    patch: Network.PatchRow_PendingValidation,
  ): string {
    return `row:${rowKey}:${DurableHandoffs.patchFields(patch)}`;
  }

  /** The debounce key of a durable sheet update — the sheet and the fields
   *  it writes, for the reason `rowPatchKey` gives. */
  static sheetPatchKey(
    sheetKey: SheetNetworkKey,
    attributes: Network.UserSheetAttributes,
  ): string {
    return `sheet:${sheetKey}:${DurableHandoffs.patchFields(attributes)}`;
  }

  /**
   * What a reported durable failure says became of the write: status `0`
   * (no answer — the fetch failed, the executor's deadline or the bridge's
   * reply timeout expired) is `"unanswered"`; a 2xx the executor refused
   * (`DebouncedRequests.classify`: a followed redirect to a login page, an
   * HTML page) is `"malformed"`, as a checked verb's `ResponseDecodeError`
   * is — the server answered, but not with the route's answer; any other
   * status is `"refused"`. Every durable verb reads its failure here.
   */
  static verdict(failure: DebouncedRequests.Failure): SdkRequestError.Answered {
    return DebouncedRequests.unreadableAnswer(failure)
      ? { outcome: "malformed" }
      : SdkRequestError.answered(failure.status);
  }

  /**
   * Hand one row PATCH (`PATCH /api/rows/{rowKey}`) to the client's
   * debounce executor under `rowPatchKey` (row + written fields — patches of
   * different fields are never coalesced), and resolve once the execution
   * that carried it answered without refusing it; reject with
   * `RowWriteError("update")` when a refusal of it reached `durable`
   * first (`outcome` per `verdict`: `"refused"`, `"unanswered"` for status
   * `0`, `"malformed"` for an answer that was not the route's). The
   * client re-reads the row on any refused row PATCH. With no `durable`
   * (a row no client routes refusals to) it resolves once handed off.
   * The `settled` of every `updateDurable` on a row.
   */
  static async persistRowPatch(
    api: TypedApi<Network.Routes>,
    durable: Option<DurableHandoffs>,
    rowKey: RowNetworkKey,
    patch: Network.PatchRow_PendingValidation,
  ): Promise<void> {
    const key = DurableHandoffs.rowPatchKey(rowKey, patch);
    const send = (requestId: Option<string>) =>
      api.debounce.patch("/api/rows/{rowKey}", {
        path: { rowKey: RowNetworkKey.fromWire(rowKey) },
        body: patch,
        options: requestId == null ? { key } : { key, requestId },
      });
    if (durable == null) {
      await send(null);
      return;
    }
    const requestId = durable.nextRequestId();
    const failure = await durable.handOff(key, requestId, () => send(requestId));
    if (failure == null) return;
    const verdict = DurableHandoffs.verdict(failure);
    throw new RowWriteError({
      step: "update",
      method: "PATCH",
      path: "/api/rows/{rowKey}",
      failure: verdict,
      reason: failure.reason ?? "",
      details: failure.details ?? "",
    });
  }

  /**
   * Fail every durable write still waiting on its verdict as unanswered
   * (status `0`) — the owning client's `dispose`, before it drops the
   * executor's refusal channel: a verdict that arrives afterwards reaches
   * no one, and a write must never settle as accepted for want of it
   * (invariant (iv)). A waiter already refused keeps its refusal.
   * Idempotent.
   */
  failAll(): void {
    for (const [key, waiters] of this.#waiters) {
      for (const waiter of waiters) {
        waiter.failure ??= {
          customKey: key,
          requestIds: [waiter.requestId],
          url: "",
          status: 0,
          reason: "client_disposed",
          details: "the client was disposed before the write's verdict reached it",
        };
      }
    }
  }

  /**
   * Record a refused debounced request against the durable writes it
   * carried (`handOff`): the waiters on its key whose `requestId` is in
   * `failure.requestIds` — the writes coalesced into the refused execution —
   * or, when the executor did not correlate (`requestIds` null), every
   * waiter on the key. Marks every carried waiter in one call, so one report
   * settles the whole execution (invariant (iii)) and a repeated report of
   * it changes nothing. Returns `true` when at least one matched — it will
   * take its change back and reject — and `false` when none did: not a
   * write of this owner, or one whose handoff already returned.
   */
  report(failure: DebouncedRequests.Failure): boolean {
    if (failure.customKey == null) return false;
    const waiters = this.#waiters.get(failure.customKey);
    if (waiters == null || waiters.size === 0) return false;
    const carried = failure.requestIds;
    let matched = false;
    for (const waiter of waiters) {
      if (carried != null && !carried.includes(waiter.requestId)) continue;
      waiter.failure = failure;
      matched = true;
    }
    return matched;
  }
}

/**
 * Every durable-write registry (`DurableHandoffs`) of one client, in one
 * place: the client's cell and row writes, and `EventsTable`'s event writes.
 * The client creates each registry here (`create`), offers every reported
 * failure to all of them (`report`), and `dispose` fails them all
 * (`failAll`) before it drops the executor's refusal channel — so no
 * durable write of any kind can hear no verdict and settle as accepted
 * (invariant (iv)). A registry made anywhere else is one `dispose` cannot
 * reach: the durable-registries hygiene test lists every construction site.
 */
export class DurableRegistries {
  readonly #all: DurableHandoffs[] = [];

  /** A new registry, reached by `report` and `failAll` from now on. */
  create(): DurableHandoffs {
    const registry = new DurableHandoffs();
    this.#all.push(registry);
    return registry;
  }

  /** How many registries `create` made. */
  get size(): number {
    return this.#all.length;
  }

  /**
   * Offer a refused debounced request to every registry
   * (`DurableHandoffs.report`); each marks the writes it carried. Returns
   * `true` when any registry matched.
   */
  report(failure: DebouncedRequests.Failure): boolean {
    let matched = false;
    for (const registry of this.#all) {
      if (registry.report(failure)) matched = true;
    }
    return matched;
  }

  /** `DurableHandoffs.failAll` on every registry. Idempotent. */
  failAll(): void {
    for (const registry of this.#all) registry.failAll();
  }
}
